import { RouteName } from "constant/routeNames";
import LazyLoading from "pages/videoPage/shared/LazyLoading";
import { FaPlay } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setVideoStart, setVideoSuccess } from "redux/VideoPlayerRedux";
import AddToFavCreator from "./AddToFavCreator";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { AssetTypes } from "constant/AssetTypes";

function VideohomeCreatorCard({ video, slug, id, season, watchProgress }: any) {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const loggedInUser = useSelector((state: any) => state?.user?.currentUser);
  const { lastWatched, creatorVideoWatchProgress } = useSelector(
    (state: any) => state?.video
  );

  // YouTube-style watch progress. Prefer the progress passed down from the
  // parent (if any), then the global per-session watch-history map (set
  // once in App.tsx), and fall back to the redux last-watch entry, which
  // is updated live while the user is actively watching in this session.
  const reduxProgressEntry = lastWatched?.find(
    (d: any) => d?.id === video?.id && d?.type === AssetTypes.CreatorVideo
  );
  const watchProgressEntry =
    watchProgress ??
    creatorVideoWatchProgress?.[video?.id] ??
    reduxProgressEntry;
  const watchProgressPercent =
    watchProgressEntry?.duration > 0
      ? Math.min(
          100,
          (watchProgressEntry.currentTime / watchProgressEntry.duration) * 100
        )
      : 0;

  const episodeOnClick = () => {
    setTimeout(async () => {
      const value = {
        ...video,
      };

      navigate(
        RouteName.CreatorPlaylistRoute.replace(
          ":slug",
          video?.creator_artist?.slug
        )
          .replace(":id", video?.season_id)
          .replace(":stream", video?.stream_key)
      );
      dispatch(setVideoStart());
      dispatch(setVideoSuccess(value));
    }, 0);
  };

  const creatorHandle = () => {
    navigate(
      RouteName.VideoCreatorHomeRoute.replace(
        ":slug",
        `@${video?.creator_artist?.slug || video?.creator?.slug}`
      )
    );
  };

  return (
   <div className="group mb-[10px] block w-full cursor-pointer rounded-2xl px-1 py-2 transition-all duration-300 ease-out hover:-translate-y-1 hover:bg-slate-800 hover:shadow-2xl hover:shadow-[var(--red)]/10">
      {/* Thumbnail */}
      <div className="bg-img relative aspect-video w-full overflow-hidden rounded-xl ring-1 ring-white/5 transition-all duration-300 group-hover:ring-2 group-hover:ring-[var(--red)]/50 max-sm:mx-auto max-sm:w-[98%]">
        <LazyLoading
          imgSrc={video?.thumbnail_url}
          imgAlt={"Card pic"}
          imgHeight={"100%"}
          imgWidth={"100%"}
          wrapperProps={{
            style: {
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              display: "block",
            },
          }}
          className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
          }}
        />

        {/* Subtle resting gradient for chip legibility */}
        <div className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-t from-black/50 via-transparent to-transparent" />

        {/* Play overlay (revealed on hover via .bg-img:hover .play-button) */}
        <div className="play-button absolute inset-0 !z-50 flex items-center justify-center rounded-xl bg-gradient-to-t from-black/70 to-transparent">
          <button
            className="flex h-12 w-12 items-center justify-center rounded-full text-white shadow-xl transition-transform duration-300 hover:scale-110 active:scale-95"
            onClick={episodeOnClick}
          >
            <FaPlay className="translate-x-[1px]" />
          </button>

          <div className="absolute bottom-3 flex w-[92%] items-center justify-between">
            <p className="rounded-md bg-black/50 px-2 py-0.5 text-[12px] font-medium text-white backdrop-blur-sm">
              {video?.runtime === 1 ? "1:00 min" : `${video?.runtime} min`}
            </p>
            <AddToFavCreator
              loggedInUser={loggedInUser}
              video={video}
              icon={true}
            />
          </div>
        </div>

        {/* YouTube-style watch progress bar, pinned to the bottom edge of the thumbnail */}
        {watchProgressPercent > 0 && (
          <div className="absolute bottom-0 left-0 z-20 h-[3px] w-full bg-white/20">
            <div
              className="h-full bg-[var(--red)]"
              style={{ width: `${watchProgressPercent}%` }}
            />
          </div>
        )}
      </div>

      {/* Bottom details */}
      <div className="relative mt-3 mb-2 h-[60px]">
        <div className="ml-1">
          <h2
            className="mb-1 w-[95%] cursor-pointer text-[14px] font-bold text-slate-200 transition-colors group-hover:text-white"
            // This project's Tailwind version (3.2.x) doesn't ship the
            // `line-clamp-*` utilities natively and the line-clamp plugin
            // isn't installed, so `line-clamp-2` silently did nothing -
            // long titles wrapped to 3+ lines and overflowed this card's
            // fixed-height layout. Setting the real CSS directly instead.
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
            data-tooltip-id={
              video?.title?.length > 40
                ? `add-song-to-favorite-${video?.title}`
                : ""
            }
          >
            {video?.title}
          </h2>
          <div className="z-50 text-sm">
            <ReactTooltip
              place="top"
              id={`add-song-to-favorite-${video?.title}`}
              content={video?.title}
              style={{ width: "180px" }}
            />
          </div>

          <div className="relative mb-0">
            <div className="creator-card-bottom-details mt-1 flex">
              <div className="my-1.5 ml-1 flex items-center justify-start max-sm:mt-[-10px]">
                <div className="h-[26px] w-[26px] flex-shrink-0 overflow-hidden rounded-full bg-black ring-1 ring-white/10 transition-all group-hover:ring-[var(--red)]/50">
                  <img
                    src={
                      video?.creator_artist?.artwork_url ||
                      video?.creator_artist?.thumbnail ||
                      video?.creator?.thumbnail_url
                    }
                    alt=""
                    className="h-full w-full object-contain"
                  />
                </div>
                <div
                  className="ml-2 hover:cursor-pointer"
                  onClick={creatorHandle}
                >
                  <p className="text-[13px] font-medium text-slate-300 transition-colors hover:text-[var(--red)]">
                    {video?.creator_artist?.name || video?.creator?.name}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VideohomeCreatorCard;